import React, { useState, useEffect } from "react";
import axios from "axios";
import { GoogleMap, LoadScript, Marker, InfoWindow } from '@react-google-maps/api';
import { FaGlobeAmericas } from 'react-icons/fa';
import AssistantMessage from './AssistantMessage';

const World = ({ apiKey, formToken, chat }) => {
  const [map, setMap] = useState(null);
  const [clickedLocation, setClickedLocation] = useState(null);
  const [infoWindowOpen, setInfoWindowOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState(chat?.messages || []);
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([
    "Tell me about the history of this place",
    "What's the local culture like?",
    "What are some interesting landmarks here?",
    "What's the climate like in this region?",
    "What languages do people speak here?"
  ]);

  // Add state for map center
  const [center, setCenter] = useState({
    lat: 20,
    lng: 0
  });

  const containerStyle = {
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0
  };

  const mapOptions = {
    mapTypeControl: true,
    mapTypeControlOptions: {
      style: window.google?.maps.MapTypeControlStyle.HORIZONTAL_BAR,
      position: window.google?.maps.ControlPosition.TOP_LEFT,
      mapTypeIds: ['roadmap', 'terrain', 'satellite', 'hybrid']
    },
    zoomControl: true,
    zoomControlOptions: {
      position: window.google?.maps.ControlPosition.LEFT_CENTER
    },
    fullscreenControl: true,
    fullscreenControlOptions: {
      position: window.google?.maps.ControlPosition.LEFT_TOP
    },
    streetViewControl: false,
    styles: [
      {
        featureType: "poi",
        elementType: "labels",
        stylers: [{ visibility: "off" }]
      }
    ]
  };

  const handleMapClick = (event) => {
    const newLocation = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng()
    };
    setClickedLocation(newLocation);
    setCenter(newLocation); // Update center when clicking
  };

  const handleQuestionSubmit = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;

    setIsLoading(true);
    setQuestion('');
    
    try {
      const response = await axios.post(`/api/v1/chats/${chat.id}/send_message`, {
        content: question,
        // Only include coordinates if a location was clicked
        ...(clickedLocation && {
          latitude: clickedLocation.lat,
          longitude: clickedLocation.lng
        }),
        authenticity_token: formToken
      });

      const message = response.data;
      
      // Update map if location focus is provided
      console.log('message', message);
      if (message.map_focus) {
        const { latitude, longitude, zoom } = message.map_focus;
        const newCenter = {
          lat: parseFloat(latitude),
          lng: parseFloat(longitude)
        };
        
        console.log('Panning to:', { ...newCenter, zoom });
        
        setCenter(newCenter); // Update center state
        setClickedLocation(newCenter); // Update marker
        
        if (map) {
          // Force the map to update its center
          map.panTo(newCenter);
          if (zoom) {
            map.setZoom(parseInt(zoom));
          }
        }
      }

      setMessages(prev => [...prev, message]);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setQuestion(suggestion);
  };

  return (
    <div className="flex h-screen">
      {/* Map Section - Takes up remaining space */}
      <div className="flex-1">
        <LoadScript googleMapsApiKey={apiKey}>
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={center} // Use center state
            zoom={3}
            options={mapOptions}
            onClick={handleMapClick}
            onLoad={setMap}
          >
            {clickedLocation && (
              <Marker position={clickedLocation} />
            )}
          </GoogleMap>
        </LoadScript>
      </div>

      {/* Chat Section - Fixed width */}
      <div className="w-96 bg-white border-l flex flex-col">
        <div className="p-4 bg-white border-b">
          <div className="flex items-center gap-2">
            <FaGlobeAmericas className="text-blue-500 text-xl" />
            <h2 className="text-xl font-bold">World Explorer Chat</h2>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Click anywhere on the map to ask questions about that location!
          </p>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((message, index) => (
            <div key={index} className={`${message.role === 'user' ? 'bg-blue-50' : 'bg-white'} p-4 rounded-lg shadow-sm`}>
              {message.role === 'user' ? (
                <div>
                  {message.latitude && (
                    <>
                      <p className="text-sm text-gray-600">Location:</p>
                      <p className="text-xs text-gray-500">
                        Lat: {Number(message.latitude).toFixed(4)}, Lng: {Number(message.longitude).toFixed(4)}
                      </p>
                    </>
                  )}
                  <p className="mt-2">{message.content}</p>
                </div>
              ) : (
                <AssistantMessage
                  message={message}
                  onFollowUpClick={setQuestion}
                  formToken={formToken}
                />
              )}
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          )}
        </div>

        {/* Input Section */}
        <div className="p-4 bg-white border-t">
          <form onSubmit={handleQuestionSubmit} className="space-y-2">
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={clickedLocation 
                ? "Ask about this location or type any geography question..."
                : "Ask any geography question or click the map to ask about a specific location!"}
              rows={3}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <div className="flex flex-wrap gap-2">
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => setQuestion(suggestion)}
                  className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full hover:bg-blue-100"
                >
                  {suggestion}
                </button>
              ))}
            </div>
            <button
              type="submit"
              disabled={isLoading || !question.trim()}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? 'Asking Ty...' : 'Ask Ty!'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default World;

